import React, {forwardRef} from 'react'

import {useController, UseControllerProps, useFormContext} from 'react-hook-form'

import InputCardMask from './InputCardMask'
import InputCvc from './InputCvc'
import InputExpirationDate from './InputExpirationDate'
import Input from '../Input'
import {IInputProps} from '../Input/Input'

interface IControlledInputProps
	extends Omit<IInputProps, 'name'>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		Omit<UseControllerProps<Record<string, any>>, 'onFocus' | 'defaultValue'> {
	placeholder: string
	option?: 'card' | 'expiration-date' | 'cvc'
}

const ControlledInput = forwardRef<HTMLInputElement, IControlledInputProps>(
	(props: IControlledInputProps, forwardedRef) => {
		const {name, rules, option, ...inputProps} = props
		const formContext = useFormContext()
		const {
			control,
			formState: {errors},
		} = formContext
		const {field} = useController({name, control, rules})

		const onBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
			if (typeof field.onBlur === 'function')
				(field as {onBlur: React.FocusEventHandler<HTMLInputElement>}).onBlur(e)
			inputProps.onBlur && inputProps.onBlur(e)
		}

		let ChosenInput = Input
		switch (option) {
			case 'card':
				ChosenInput = InputCardMask
				break
			case 'expiration-date':
				ChosenInput = InputExpirationDate
				break
			case 'cvc':
				ChosenInput = InputCvc
				break
			default:
				break
		}

		return (
			<ChosenInput
				{...inputProps}
				{...field}
				onBlur={onBlur}
				isError={!!errors[name]?.message}
				ref={forwardedRef}
			/>
		)
	},
)

ControlledInput.displayName = 'ControlledInput'

interface IFormTextInputProps extends IControlledInputProps {}

const FormTextInput = forwardRef<HTMLInputElement, IFormTextInputProps>((props, forwardedRef) => {
	const {name, ...inputProps} = props
	const formContext = useFormContext()

	if (!formContext || !name) {
		const errorMessage = !name
			? 'Form field must have a "name" prop!'
			: 'Form field must be a descendant of `FormProvider` as it uses `useFormContext`!'
		return <Input {...inputProps} error={errorMessage} />
	}

	return <ControlledInput {...props} ref={forwardedRef} />
})

FormTextInput.displayName = 'FormTextInput'

export default FormTextInput
